import React, { useEffect, useState } from 'react';
import clientPortalService from 'services/clientPortalService';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const CommunicationCenter = ({ language, userId }) => {
  const [activeTab, setActiveTab] = useState('messages');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        
        // Fetch conversations
        const convsData = await clientPortalService?.getConversations(userId);
        const formattedConvs = convsData?.map(conv => ({
          id: conv?.id,
          lawyer: conv?.lawyer?.fullName || 'Lawyer',
          lawyerSw: conv?.lawyer?.fullName || 'Mwanasheria',
          avatar: conv?.lawyer?.avatarUrl || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
          lastMessage: conv?.lastMessage || 'No messages yet',
          lastMessageSw: conv?.lastMessage || 'Hakuna ujumbe bado',
          timestamp: getRelativeTime(conv?.lastMessageAt),
          unreadCount: conv?.unreadCount || 0,
          online: false,
          caseId: conv?.case?.id
        })) || [];
        setConversations(formattedConvs);
        
        if (formattedConvs?.length > 0 && !selectedConversation) {
          setSelectedConversation(formattedConvs?.[0]?.id);
        }

        // Fetch consultations/appointments
        const consultsData = await clientPortalService?.getConsultations(userId);
        const formattedConsults = consultsData?.map(consult => ({
          id: consult?.id,
          title: consult?.title,
          titleSw: consult?.title,
          lawyer: consult?.lawyer?.fullName || 'Lawyer',
          date: new Date(consult?.consultationDate)?.toLocaleDateString('en-GB'),
          time: consult?.consultationTime,
          duration: consult?.duration,
          location: consult?.location,
          locationSw: consult?.location,
          status: consult?.status,
          type: consult?.consultationType,
          description: consult?.description,
          descriptionSw: consult?.description
        })) || [];
        setAppointments(formattedConsults);
      } catch (error) {
        console.error('Error fetching communication data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Setup real-time subscription for messages
    if (userId) {
      const unsubscribe = clientPortalService?.subscribeToUpdates(userId, {
        onMessageUpdate: (payload) => {
          // Refresh messages if in current conversation
          if (payload?.new?.conversationId === selectedConversation) {
            fetchMessages(selectedConversation);
          }
          // Refresh conversations list
          fetchData();
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [userId]);

  useEffect(() => {
    const fetchMessages = async (conversationId) => {
      if (!conversationId) return;
      
      try {
        const msgsData = await clientPortalService?.getMessages(conversationId);
        const formattedMsgs = msgsData?.map(msg => ({
          id: msg?.id,
          senderId: msg?.senderId === userId ? 'client' : 'lawyer',
          senderName: msg?.senderId === userId ? 'You' : msg?.sender?.fullName,
          content: msg?.content,
          contentSw: msg?.content,
          timestamp: new Date(msg?.createdAt)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          date: 'Today',
          type: msg?.messageType,
          attachment: msg?.attachmentName ? {
            name: msg?.attachmentName,
            size: msg?.attachmentSize,
            type: msg?.attachmentType
          } : null
        })) || [];
        setMessages(formattedMsgs);
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };

    if (selectedConversation) {
      fetchMessages(selectedConversation);
    }
  }, [selectedConversation, userId]);

  const getRelativeTime = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  };

  const translations = {
    en: {
      messages: "Messages",
      appointments: "Appointments",
      consultations: "Consultations",
      typeMessage: "Type your message...",
      send: "Send",
      scheduleAppointment: "Schedule Appointment",
      requestConsultation: "Request Consultation",
      online: "Online",
      offline: "Offline",
      confirmed: "Confirmed",
      pending: "Pending",
      cancelled: "Cancelled",
      inPerson: "In Person",
      videoCall: "Video Call",
      phoneCall: "Phone Call",
      today: "Today",
      yesterday: "Yesterday",
      attachment: "Attachment",
      noMessages: "No messages yet",
      startConversation: "Start a conversation with your lawyer",
      upcomingAppointments: "Upcoming Appointments",
      noAppointments: "No appointments scheduled"
    },
    sw: {
      messages: "Ujumbe",
      appointments: "Miadi",
      consultations: "Mashauriano",
      typeMessage: "Andika ujumbe wako...",
      send: "Tuma",
      scheduleAppointment: "Panga Madi",
      requestConsultation: "Omba Ushauri",
      online: "Mtandaoni",
      offline: "Nje ya Mtandao",
      confirmed: "Imethibitishwa",
      pending: "Inasubiri",
      cancelled: "Imeghairiwa",
      inPerson: "Kwa Uso",
      videoCall: "Simu ya Video",
      phoneCall: "Simu ya Sauti",
      today: "Leo",
      yesterday: "Jana",
      attachment: "Kiambatisho",
      noMessages: "Hakuna ujumbe bado",
      startConversation: "Anza mazungumzo na wakili wako",
      upcomingAppointments: "Miadi Ijayo",
      noAppointments: "Hakuna miadi iliyopangwa"
    }
  };

  const t = translations?.[language];

  const selectedConv = conversations?.find(c => c?.id === selectedConversation);

  const handleSendMessage = () => {
    if (newMessage?.trim()) {
      // Mock send message functionality
      console.log('Sending message:', newMessage);
      setNewMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const getAppointmentIcon = (type) => {
    switch (type) {
      case 'video_call':
        return 'Video';
      case 'phone_call':
        return 'Phone';
      case 'in_person':
        return 'Users';
      default:
        return 'Calendar';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-success-100 text-success-700';
      case 'pending':
        return 'bg-warning-100 text-warning-700';
      case 'cancelled':
        return 'bg-error-100 text-error-700';
      default:
        return 'bg-secondary-100 text-secondary-700';
    }
  };

  const renderMessages = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    return (
      <div className="flex h-96 lg:h-[600px] bg-surface rounded-lg shadow-card overflow-hidden">
        {/* Conversations List */}
        <div className="w-full lg:w-1/3 border-r border-border">
          <div className="p-4 border-b border-border">
            <h3 className="font-medium text-text-primary">{t?.messages}</h3>
          </div>
          <div className="overflow-y-auto h-full">
            {conversations?.map(conv => (
              <div
                key={conv?.id}
                onClick={() => setSelectedConversation(conv?.id)}
                className={`
                  p-4 border-b border-border cursor-pointer transition-smooth
                  ${selectedConversation === conv?.id ? 'bg-primary-50' : 'hover:bg-secondary-50'}
                `}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Image
                      src={conv?.avatar}
                      alt={conv?.lawyer}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    {conv?.online && (
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-success-500 rounded-full border-2 border-white"></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-text-primary truncate">
                        {language === 'en' ? conv?.lawyer : conv?.lawyerSw}
                      </h4>
                      {conv?.unreadCount > 0 && (
                        <span className="bg-primary text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                          {conv?.unreadCount}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-secondary truncate">
                      {language === 'en' ? conv?.lastMessage : conv?.lastMessageSw}
                    </p>
                    <p className="text-xs text-text-secondary mt-1">{conv?.timestamp}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Chat Area */}
        <div className="hidden lg:flex lg:flex-col lg:flex-1">
          {selectedConv ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <Image
                    src={selectedConv?.avatar}
                    alt={selectedConv?.lawyer}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-medium text-text-primary">
                      {language === 'en' ? selectedConv?.lawyer : selectedConv?.lawyerSw}
                    </h3>
                    <p className="text-sm text-text-secondary">
                      {selectedConv?.online ? t?.online : t?.offline}
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages?.map(message => (
                  <div
                    key={message?.id}
                    className={`flex ${message?.senderId === 'client' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`
                      max-w-xs lg:max-w-md px-4 py-2 rounded-lg
                      ${message?.senderId === 'client' ?'bg-primary text-white' :'bg-secondary-100 text-text-primary'
                      }
                    `}>
                      <p className="text-sm">
                        {language === 'en' ? message?.content : message?.contentSw}
                      </p>
                      {message?.attachment && (
                        <div className="mt-2 p-2 bg-white bg-opacity-20 rounded flex items-center gap-2">
                          <Icon name="Paperclip" size={16} />
                          <span className="text-xs">{message?.attachment?.name}</span>
                        </div>
                      )}
                      <p className="text-xs opacity-75 mt-1">{message?.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e?.target?.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={t?.typeMessage}
                    className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    onClick={handleSendMessage}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth"
                  >
                    <Icon name="Send" size={16} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Icon name="MessageSquare" size={48} className="mx-auto text-text-secondary mb-4" />
                <p className="text-text-secondary">{t?.startConversation}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAppointments = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-heading font-semibold text-text-primary">{t?.upcomingAppointments}</h3>
        <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth">
          {t?.scheduleAppointment}
        </button>
      </div>

      {appointments?.length === 0 ? (
        <div className="text-center py-12 bg-surface rounded-lg shadow-card">
          <Icon name="Calendar" size={48} className="mx-auto text-text-secondary mb-4" />
          <p className="text-text-secondary">{t?.noAppointments}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {appointments?.map(appointment => (
            <div key={appointment?.id} className="bg-surface rounded-lg shadow-card p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Icon name={getAppointmentIcon(appointment?.type)} size={24} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-text-primary">
                      {language === 'en' ? appointment?.title : appointment?.titleSw}
                    </h4>
                    <p className="text-sm text-text-secondary mt-1">
                      {language === 'en' ? appointment?.description : appointment?.descriptionSw}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment?.status)}`}>
                  {t?.[appointment?.status]}
                </span>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-text-secondary">Date:</span>
                  <p className="font-medium text-text-primary">{appointment?.date}</p>
                </div>
                <div>
                  <span className="text-text-secondary">Time:</span>
                  <p className="font-medium text-text-primary">{appointment?.time}</p>
                </div>
                <div>
                  <span className="text-text-secondary">Duration:</span>
                  <p className="font-medium text-text-primary">{appointment?.duration}</p>
                </div>
                <div>
                  <span className="text-text-secondary">Location:</span>
                  <p className="font-medium text-text-primary">
                    {language === 'en' ? appointment?.location : appointment?.locationSw}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <p className="text-sm text-text-secondary">
                  With {appointment?.lawyer}
                </p>
                <div className="flex gap-2">
                  <button className="px-3 py-1 bg-secondary-100 text-text-secondary rounded hover:bg-secondary-200 transition-smooth">
                    Reschedule
                  </button>
                  <button className="px-3 py-1 bg-error-100 text-error-700 rounded hover:bg-error-200 transition-smooth">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-text-primary">Communication Center</h1>
      </div>
      {/* Tabs */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="flex space-x-1 mb-6">
          {[
            { id: 'messages', label: t?.messages, icon: 'MessageSquare' },
            { id: 'appointments', label: t?.appointments, icon: 'Calendar' }
          ]?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-smooth
                ${activeTab === tab?.id
                  ? 'bg-primary text-white' :'text-text-secondary hover:bg-secondary-100'
                }
              `}
            >
              <Icon name={tab?.icon} size={16} />
              {tab?.label}
            </button>
          ))}
        </div>

        {activeTab === 'messages' && renderMessages()}
        {activeTab === 'appointments' && renderAppointments()}
      </div>
    </div>
  );
};

export default CommunicationCenter;